import React, { useMemo, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BLOG_POSTS, slugify } from '../data/blogContent';
import logoBlack from '../assets/images/logo-black.png';
import './BlogPage.css';
import './EnhancedMarketingLandingPage.css';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const post = useMemo(
    () => BLOG_POSTS.find(p => (p.slug || slugify(p.title)) === slug),
    [slug]
  );

  const related = useMemo(() => {
    if (!post) return [];
    return BLOG_POSTS
      .filter(p => p.id !== post.id && p.series === post.series)
      .slice(0, 3);
  }, [post]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const keyPointsList = post.keyPoints
    .split(/\.\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  return (
    <>
      <Helmet>
        <title>{post.title} - BSO Blog</title>
        <meta name="description" content={post.hook.slice(0, 160)} />
      </Helmet>

      <div className="landing blog-page">
        {/* Header */}
        <header className="header header-scrolled">
          <div className="container">
            <div className="nav-brand">
              <Link to="/"><img src={logoBlack} alt="BigSystemOne" className="logo" /></Link>
            </div>
            <nav className="nav">
              <Link to="/#features">Features</Link>
              <Link to="/#how-it-works">How It Works</Link>
              <Link to="/#pricing">Pricing</Link>
              <Link to="/blog">Blog</Link>
              <Link to="/login" className="nav-cta">Access Platform</Link>
            </nav>
          </div>
        </header>

        {/* Post Hero */}
        <section className="blog-post-hero">
          <div className="container">
            <Link to="/blog" className="blog-back-link">&larr; Back to Blog</Link>
            <h1>{post.title}</h1>
            <div className="blog-post-meta">
              <span className="blog-badge blog-badge-series">{post.series}</span>
              <span className="blog-badge blog-badge-category">{post.category}</span>
              <span className="blog-badge blog-badge-category">{post.audience}</span>
            </div>
          </div>
        </section>

        {/* Post Content */}
        <article className="blog-post-content">
          <h2>Introduction</h2>
          <p>{post.hook}</p>

          <h2>Key Insights</h2>
          <ul>
            {keyPointsList.map((point, i) => (
              <li key={i}>{point.endsWith('.') ? point : point + '.'}</li>
            ))}
          </ul>

          <div className="blog-cta-box">
            <h3>Take Action</h3>
            <p>{post.cta}</p>
            <Link to="/login" className="blog-cta-btn">Get Started with BSO</Link>
          </div>
        </article>

        {/* Related Posts */}
        {related.length > 0 && (
          <section className="blog-related">
            <h2>More from {post.series}</h2>
            <div className="blog-related-grid">
              {related.map(rp => {
                const rpSlug = rp.slug || slugify(rp.title);
                return (
                  <Link to={`/blog/${rpSlug}`} className="blog-card" key={rp.id}>
                    <div className="blog-card-badges">
                      <span className="blog-badge blog-badge-category">{rp.category}</span>
                    </div>
                    <h3>{rp.title}</h3>
                    <p className="blog-card-excerpt">
                      {rp.hook.length > 120 ? rp.hook.slice(0, 120) + '...' : rp.hook}
                    </p>
                    <span className="blog-card-link">Read More &rarr;</span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              <div className="footer-brand">
                <img src={logoBlack} alt="BigSystemOne" className="footer-logo" />
                <p>Restaurant Reputation Management Platform</p>
              </div>
              <div className="footer-links">
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/terms">Terms of Service</Link>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 BigSystemOne. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPostPage;
